import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Account } from '../../models/account.model';

export interface UserResponse {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName?: string;
  [key: string]: any;
}

export interface EmailDosResponse {
  user: UserResponse;
  token: string;
}

// Minimal customer-related interfaces (expand when schema known)
export interface CustomerSummary {
  id?: string;
  name?: string;
  externalId?: string;
  email?: string;
  [key: string]: any;
}

export interface CustomerDetail extends CustomerSummary {
  addresses?: any[];
  contacts?: any[];
  status?: string | number;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private apiUrl = environment.api;

  constructor(private http: HttpClient) {}

  // Centralized default headers for JSON requests to mirror the working app
  private defaultHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
    });
  }

  getUserById(userId?: string): Observable<UserResponse> {
    const id =
      userId ||
      localStorage.getItem('cid') ||
      (environment as any).customerId ||
      '';
    const url = `${this.apiUrl}/api/v1/users/${id}`;
    console.log('[UserService] GET user by ID (cid fallback to env):', {
      url,
      id,
    });
    return this.http.get<UserResponse>(url);
  }

  /**
   * Fetch user by externalId using endpoint: /api/v1/users/externalId/{externalId}
   */
  getUserByExternalId(externalId: string): Observable<UserResponse> {
    const safeExternalId = externalId || '';
    const url = `${this.apiUrl}/api/v1/users/externalId/${safeExternalId}`;
    console.log('[UserService] GET user by externalId:', {
      url,
      externalId: safeExternalId,
    });
    return this.http.get<UserResponse>(url);
  }

  /**
   * Fetch account by id using endpoint: /api/v1/accounts/{accountId}
   */
  getAccountById(accountId: string): Observable<Account> {
    const id = accountId || '';
    const url = `${this.apiUrl}/api/v1/accounts/${id}`;
    console.log('[UserService] GET account by id:', { url, accountId: id });
    return this.http.get<Account>(url);
  }

  showAccountID(id: any) {
    const url = `${this.apiUrl}/api/v1/accounts/${id}`;
    return this.http.get(url);
  }

  getUserByEmailDos(email: string): Observable<EmailDosResponse> {
    const url = `${this.apiUrl}/api/v1/users/email-dos/${email}`;
    console.log('[UserService] GET user email-dos:', { url, email });
    return this.http.get<EmailDosResponse>(url);
  }

  createCustomer(customer: any) {
    const url = `${this.apiUrl}/api/v1/customers/insert-customer`;
    console.log('[UserService] POST create customer:', { url, customer });
    return this.http.post(url, customer, { headers: this.defaultHeaders() });
  }

  showListCustomer(scid: string, accid: string): Observable<CustomerSummary[]> {
    const url = `${this.apiUrl}/api/v1/customers/get-all-customer/${scid}/${accid}`;
    console.log('[UserService] GET list customers:', { url, scid, accid });
    return this.http.get<CustomerSummary[]>(url);
  }

  getCustomer(
    scid: string,
    usid: string,
    data: any,
  ): Observable<CustomerDetail> {
    const url = `${this.apiUrl}/api/v1/customers/customers-getbyuserid/${scid}/${usid}`;
    console.log('[UserService] POST get customer by userId:', {
      url,
      scid,
      usid,
      body: data,
    });
    return this.http.post<CustomerDetail>(url, data, {
      headers: this.defaultHeaders(),
    });
  }

  getByServiceCenter(): Observable<CustomerSummary[]> {
    const scid = localStorage.getItem('scid') || '';
    const url = `${this.apiUrl}/api/v1/customers/service-center/${scid}`;
    console.log('[UserService] GET customers by service center:', {
      url,
      scid,
    });
    return this.http.get<CustomerSummary[]>(url);
  }

  getById(customerId: string): Observable<CustomerDetail> {
    const url = `${this.apiUrl}/api/v1/customers/${customerId}`;
    console.log('[UserService] GET customer by id:', { url, customerId });
    return this.http.get<CustomerDetail>(url);
  }

  getServiceRequestById(serviceRequestId: string): Observable<any> {
    const id = serviceRequestId || '';
    const url = `${this.apiUrl}/api/v1/service-requests/${id}`;
    console.log('[UserService] GET service request by id:', {
      url,
      serviceRequestId: id,
    });
    return this.http.get<any>(url, { headers: this.defaultHeaders() });
  }

  showDetailsCustomer(
    serviceCenterId: string,
    UserId: string,
  ): Observable<CustomerDetail> {
    const url = `${this.apiUrl}/api/v1/customers/customers-getbyuserid/${serviceCenterId}/${UserId}`;
    console.log('[UserService] POST show details customer:', {
      url,
      serviceCenterId,
      UserId,
    });
    return this.http.post<CustomerDetail>(
      url,
      {},
      { headers: this.defaultHeaders() },
    );
  }

  /**
   * New: call the paginated customers detail endpoint and send customerId and serviceCenterId
   * The payload will prefer values from localStorage (cid/scid) as requested.
   */
  getCustomerAccountByUserId(
    serviceCenterId?: string,
    userId?: string,
  ): Observable<any> {
    const scid = localStorage.getItem('scid') || serviceCenterId || '';
    const cid = localStorage.getItem('cid') || userId || '';

    const url = `${this.apiUrl}/api/v1/customers/get-customers-detail-paginated`;
    const body = {
      customerId: cid,
      serviceCenterId: scid,
    };

    console.log(
      '[UserService] POST getCustomerAccountByUserId -> paginated endpoint:',
      {
        url,
        body,
      },
    );

    return this.http
      .post<any>(url, body, { headers: this.defaultHeaders() })
      .pipe(
        tap((resp) =>
          console.log(
            '[UserService] getCustomerAccountByUserId response:',
            resp,
          ),
        ),
        catchError((err) => {
          console.error('[UserService] getCustomerAccountByUserId error:', err);
          throw err;
        }),
      );
  }
}
